import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useFormik } from "formik";
import * as Yup from "yup";
import InputField from "../Form/InputField";
import TextArea from "../Form/TextArea";
import Button from "../button/Button";
import {
  create_Partnership_API,
  Edit_Partnership_API,
} from "../../API/PartnershipAPI/PartnershipAPI";
import FileDropper from "../dropers/FileDropper";
import Loader from "../Loader/Loader";
import SelectFieldMultiple from "../Form/selectFieldMultiple";

const formatDate = (dateString) => {
  if (!dateString) return "";
  const date = new Date(dateString);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const AddPart = ({
  pageNumber,
  queryClient,
  isOpen,
  setIsOpen,
  socialMediaData,
  limit,
  isEditMode, // Flag to indicate whether it's edit mode or create mode
  PartData,
}) => {
  const [btnLoader, setBtnLoader] = useState(false);

  // Formik setup
  const formik = useFormik({
    initialValues: {
      websiteLink: "",
      title: "",
      companyName: "",
      amount: "",
      description: "",
      deliverables: "",
      platform: [], // Change to an array for multiple selection
      companyLogo: null,
      deadline: "", // Add deadline field
      isEditMode,
    },
    validationSchema: Yup.object({
      websiteLink: Yup.string().url().required("website url required"),
      title: Yup.string().required("Title is required"),
      companyName: Yup.string().required("Company name is required"),
      amount: Yup.number().required("Amount is required"),
      description: Yup.string().required("Description is required"),
      deliverables: Yup.string().required("Deliverables are required"),
      platform: Yup.array().min(1, "At least one platform is required"), // Validate array
      companyLogo: Yup.string().required("Company logo is required"),
      deadline: Yup.date().required("Deadline is required"), // Validate date
    }),
    onSubmit: async (values) => {
      setBtnLoader(true);
      const payload = {
        websiteLink: values.websiteLink,
        title: values.title,
        description: values.description,
        companyLogo: values.companyLogo,
        partnerShipCompanyName: values.companyName,
        deliverables: values.deliverables,
        platForm: values.platform, // Already an array
        amount: values.amount,
        deadline: values.deadline, // Include deadline in payload
        isEditMode: values.isEditMode,
      };

      try {
        if (payload?.isEditMode) {
          const isEditMode = payload.isEditMode;
          const payloadIs = {
            websiteLink: payload.websiteLink,
            title: payload.title,
            description: payload.description,
            companyLogo: payload.companyLogo,
            partnerShipCompanyName: payload.partnerShipCompanyName,
            deliverables: payload.deliverables,
            platForm: payload.platForm,
            amount: payload.amount,
            deadline: payload.deadline, // Include deadline in edit payload
          };
          await Edit_Partnership_API({
            ...payloadIs,
            partnerShipId: isEditMode,
          }).then((res) => {
            setBtnLoader(false);
            setIsOpen(false);
            queryClient.setQueryData(
              ["partnership", pageNumber, limit],
              (oldData) => {
                if (!oldData) return oldData;
                return {
                  ...oldData,
                  data: {
                    ...oldData.data,
                    data: oldData.data.data.map((item) => {
                      if (item._id.toString() === res.data._id.toString())
                        return res.data;
                      return item;
                    }),
                  },
                };
              }
            );
            formik.resetForm(); // Reset form after edit operation
          });
        } else {
          const payloadIs = {
            websiteLink: payload.websiteLink,
            title: payload.title,
            description: payload.description,
            companyLogo: payload.companyLogo,
            partnerShipCompanyName: payload.partnerShipCompanyName,
            deliverables: payload.deliverables,
            platForm: payload.platForm,
            amount: payload.amount,
            deadline: payload.deadline, // Include deadline in create payload
          };
          await create_Partnership_API(payloadIs).then((res) => {
            setBtnLoader(false);
            setIsOpen(false);
            queryClient.setQueryData(
              ["partnership", pageNumber, limit],
              (oldData) => {
                if (!oldData) return oldData;
                return {
                  ...oldData,
                  data: {
                    ...oldData.data,
                    data: [res.data, ...oldData.data.data],
                  },
                };
              }
            );
            formik.resetForm(); // Reset form after create operation
          });
        }
      } catch (error) {
        setBtnLoader(false);
        console.error("Error handling form submission", error);
      }
    },
  });

  const editPart = (partnership) => {
    formik.setValues({
      websiteLink: partnership.websiteLink,
      title: partnership.title,
      companyName: partnership.partnerShipCompanyName,
      amount: partnership.amount,
      description: partnership.description,
      deliverables: partnership.deliverables,
      platform: partnership.platForm, // Already an array
      companyLogo: partnership.companyLogo, // Handle file upload logic if required
      deadline: formatDate(partnership.deadline), // Format the deadline
      isEditMode,
    });
  };

  useEffect(() => {
    if (isEditMode) {
      let partnershipData = PartData?.data?.data?.find(
        (part) => part._id.toString() === isEditMode.toString()
      );
      console.log("partnershipData", partnershipData);
      editPart(partnershipData);
    }
  }, [isEditMode]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[999] flex items-center justify-center bg-black bg-opacity-60 backdrop-blur-sm"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="relative w-[96%] sm:w-1/2 h-[90vh] overflow-auto rounded-lg bg-white shadow-sm"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="px-8 py-4">
                <h5 className="text-xl font-medium text-slate-800">
                  {isEditMode ? "Edit Partnership" : "Add Partnership"}
                </h5>

                <form onSubmit={formik.handleSubmit} className="space-y-4">
                  {/* Partnership Title */}
                  <InputField
                    label="Partnership Title"
                    name="title"
                    value={formik.values.title}
                    onChange={formik.handleChange}
                    error={formik.errors.title}
                    touch={formik.touched.title}
                  />

                  {/* Company Name */}
                  <InputField
                    label="Partnership Company Name"
                    name="companyName"
                    value={formik.values.companyName}
                    onChange={formik.handleChange}
                    error={formik.errors.companyName}
                    touch={formik.touched.companyName}
                  />

                  <InputField
                    label="Website Link"
                    name="websiteLink"
                    value={formik.values.websiteLink}
                    onChange={formik.handleChange}
                    error={formik.errors.websiteLink}
                    touch={formik.touched.websiteLink}
                  />

                  {/* Amount */}
                  <InputField
                    label="Amount"
                    name="amount"
                    value={formik.values.amount}
                    onChange={formik.handleChange}
                    error={formik.errors.amount}
                    touch={formik.touched.amount}
                  />

                  {/* Description */}
                  <TextArea
                    label="Description"
                    name="description"
                    value={formik.values.description}
                    onChange={formik.handleChange}
                    error={formik.errors.description}
                    touch={formik.touched.description}
                  />

                  {/* Deliverables */}
                  <TextArea
                    label="Deliverables"
                    name="deliverables"
                    value={formik.values.deliverables}
                    onChange={formik.handleChange}
                    error={formik.errors.deliverables}
                    touch={formik.touched.deliverables}
                  />

                  {/* Platform */}
                  <SelectFieldMultiple
                    label="Platform"
                    name="platform"
                    options={socialMediaData}
                    value={formik.values.platform}
                    onChange={formik.handleChange}
                    error={formik.errors.platform}
                    touch={formik.touched.platform}
                  />
                  {formik.values.companyLogo && <FileDropper formik={formik} />}
                  {formik.values.companyLogo || <FileDropper formik={formik} />}

                  {/* Deadline */}
                  <InputField
                    label="Deadline"
                    name="deadline"
                    type="date"
                    value={formik.values.deadline}
                    onChange={formik.handleChange}
                    error={formik.errors.deadline}
                    touch={formik.touched.deadline}
                    min={new Date().toISOString().split("T")[0]} // Sets minimum date to today
                  />

                  {/* Buttons */}
                  <div className="mt-4 flex justify-center gap-4">
                    {btnLoader ? (
                      <Loader />
                    ) : (
                      <>
                        <Button
                          type="submit"
                          customClass="!max-w-fit"
                          text={isEditMode ? "Update" : "Submit"}
                        />
                        <Button
                          onClick={() => {
                            setIsOpen(false);
                            formik.resetForm();
                          }}
                          customClass="!max-w-fit !bg-black !text-white"
                          text="Cancel"
                        />
                      </>
                    )}
                  </div>
                </form>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default AddPart;
